import { FilterModel } from '../../filter/domain/filterModel';
import {
  metaPillCount,
  metaPillToFilter,
} from '../../filter/domain/metaPillState';
import { DateTime } from 'luxon';
import { DATE_FORMAT_MMDDYYYYLUXON } from '../../utils';
import { Severity } from '../../graphql/enums';
import { ASSET_CHAT_STREAM_FILTERS, AssetChatStreamFilter } from './assetChat';

export enum ActivityOrderBy {
  MostRecentReported = 'MOST_RECENT_REPORTED',
  MostRecentUpdated = 'MOST_RECENT_UPDATED',
  ReminderDate = 'REMINDER_DATE',
  CreationDate = 'CREATION_DATE',
}

export enum ActivityFilterKey {
  Activity = 'ACTIVITY_FILTER',
  AssetActivity = 'ASSET_ACTIVITY_FILTER',
  // The People page's per-person activity column (plan 0030, Phase 4). Needs
  // its own storage slot — see useActivityListFilter/useSetActivityListFilter
  // — so opening a person's activity can never share state with the asset
  // feed.
  AccountActivity = 'ACCOUNT_ACTIVITY_FILTER',
  // Asset chat (plan 0035). Its own slot for the same reason AccountActivity
  // has one, and one more: chat is presented as the asset's full
  // conversation, so inheriting the activity list's persisted severity /
  // group / label / keyword / date filters would silently show a partial
  // history — or "Nothing here yet" — with nothing on screen explaining why.
  // Chat's own visible filter ships with the chat header.
  AssetChat = 'ASSET_CHAT_FILTER',
}

export enum DateTypeFilter {
  ALL = 'all',
  TODAY = 'today',
  THISWEEK = 'thisWeek',
  THISMONTH = 'thisMonth',
  CUSTOMDATE = 'customDate',
}

export enum ActivityOptions {
  EVERYTHING = 'EVERYTHING',
  MENTIONED = 'MENTIONED',
  PINNED = 'PINNED',
  SCHEDULED = 'SCHEDULED',
  NOT_SCHEDULED = 'NOT_SCHEDULED',
}

export class ActivityFilterModel implements FilterModel {
  orderBy?: ActivityOrderBy;
  severity?: string[];
  assetName?: string;
  groupIds?: string[];
  includeAllGroups?: boolean;
  includeUngrouped?: boolean;
  labelIds?: string[];
  includeAllLabels?: boolean;
  includeUnlabeled?: boolean;
  keyword?: string;
  dateType?: DateTypeFilter;
  startDate?: string;
  endDate?: string;
  isCombinedGroupsFilter?: boolean;
  activityOption?: ActivityOptions;
  /**
   * UI-only marker used by the Campaigns toggle to record OFF state.
   * Convention: contains `'CAMPAIGN'` when the toggle is OFF; undefined / empty
   * (or missing `'CAMPAIGN'`) when ON. Not passed to findIssues — the main feed
   * always excludes raw `CAMPAIGN` issue rows via `excludeActivityTypes` (see
   * `getActivityFilter`), since campaign activity surfaces only through the
   * aggregated query.
   */
  activityTypes?: string[];
  /** Native Chat selection; the host maps it to stream query inputs. */
  chatStreamFilter?: AssetChatStreamFilter;

  constructor(data?: any) {
    // Convert string orderBy back to enum if needed
    if (data?.orderBy && typeof data.orderBy === 'string') {
      // Validate that the string is a valid enum value
      const validOrderByValues = Object.values(ActivityOrderBy);
      if (validOrderByValues.includes(data.orderBy as ActivityOrderBy)) {
        this.orderBy = data.orderBy as ActivityOrderBy;
      } else {
        console.warn('Invalid orderBy value:', data.orderBy, 'falling back to default');
        this.orderBy = ActivityOrderBy.MostRecentUpdated;
      }
    } else {
      this.orderBy = data?.orderBy ?? ActivityOrderBy.MostRecentUpdated;
    }

    this.severity = data?.severity ?? Object.values(Severity);
    this.assetName = data?.assetName;
    this.groupIds = data?.groupIds;
    this.labelIds = data?.labelIds;
    this.activityOption = data?.activityOption ?? ActivityOptions.EVERYTHING;
    this.keyword = data?.keyword;
    this.dateType = data?.dateType ?? DateTypeFilter.ALL;
    this.startDate = data?.startDate ?? '';
    this.endDate = data?.endDate ?? '';
    this.isCombinedGroupsFilter = data?.isCombinedGroupsFilter;
    this.activityOption = data?.activityOption ?? ActivityOptions.EVERYTHING;
    this.activityTypes = data?.activityTypes;
    this.chatStreamFilter = ASSET_CHAT_STREAM_FILTERS.find(
      (selection) => selection === data?.chatStreamFilter
    );

    // Migrate old format: if new pill fields are absent, infer from the legacy arrays.
    // Old groupIds=[] or undefined → both pills on (default = show everything).
    // Old groupIds=['x'] → specific group selected, both meta-pills off.
    if (data?.includeAllGroups !== undefined || data?.includeUngrouped !== undefined) {
      this.includeAllGroups = data.includeAllGroups ?? true;
      this.includeUngrouped = data.includeUngrouped ?? true;
    } else {
      const hasSpecificGroups = data?.groupIds && data.groupIds.length > 0;
      this.includeAllGroups = !hasSpecificGroups;
      this.includeUngrouped = !hasSpecificGroups;
    }

    if (data?.includeAllLabels !== undefined || data?.includeUnlabeled !== undefined) {
      this.includeAllLabels = data.includeAllLabels ?? true;
      this.includeUnlabeled = data.includeUnlabeled ?? true;
    } else {
      const hasSpecificLabels = data?.labelIds && data.labelIds.length > 0;
      this.includeAllLabels = !hasSpecificLabels;
      this.includeUnlabeled = !hasSpecificLabels;
    }
  }

  public toJSON(): any {
    return {
      orderBy: this.orderBy,
      severity: this.severity,
      assetName: this.assetName,
      groupIds: this.groupIds,
      includeAllGroups: this.includeAllGroups,
      includeUngrouped: this.includeUngrouped,
      labelIds: this.labelIds,
      includeAllLabels: this.includeAllLabels,
      includeUnlabeled: this.includeUnlabeled,
      keyword: this.keyword,
      dateType: this.dateType,
      startDate: this.startDate,
      endDate: this.endDate,
      isCombinedGroupsFilter: this.isCombinedGroupsFilter,
      activityOption: this.activityOption,
      activityTypes: this.activityTypes,
      chatStreamFilter: this.chatStreamFilter,
    };
  }

  public getFilter(): any {
    const filter: any = {};

    if (this.severity) {
      filter.severity = this.severity;
    }

    // Groups: map pill state → API fields (shared metaPillToFilter). The
    // combined-groups flag rides along only when specific groups are sent.
    const groups = metaPillToFilter({
      includeAll: this.includeAllGroups ?? true,
      includeNone: this.includeUngrouped ?? true,
      items: (this.groupIds ?? []).filter((g): g is string => !!g),
    });
    if (groups.ids) {
      filter.groupIds = groups.ids;
      if (this.isCombinedGroupsFilter) {
        filter.isCombinedGroupsFilter = this.isCombinedGroupsFilter;
      }
    }
    if (groups.includeNone !== undefined) {
      filter.includeUngrouped = groups.includeNone;
    }

    // Labels: map pill state → API fields (shared metaPillToFilter).
    const labels = metaPillToFilter({
      includeAll: this.includeAllLabels ?? true,
      includeNone: this.includeUnlabeled ?? true,
      items: (this.labelIds ?? []).filter((l): l is string => !!l),
    });
    if (labels.ids) {
      filter.labelIds = labels.ids;
    }
    if (labels.includeNone !== undefined) {
      filter.includeUnlabeled = labels.includeNone;
    }

    if (this.keyword) {
      filter.keyword = this.keyword;
    }

    if (this.activityOption && this.activityOption !== ActivityOptions.EVERYTHING) {
      filter.activityOption = this.activityOption;
    }

    const dateRange = this.getDateRange();
    if (dateRange?.start && dateRange?.end) {
      filter.startDate = dateRange?.start;
      filter.endDate = dateRange?.end;
    }

    return filter;
  }

  public getTypesCount(): number {
    let count = 0;

    if (this.severity) {
      count += this.severity.length;
    }

    if (this.activityOption && this.activityOption !== ActivityOptions.EVERYTHING) {
      count += 1;
    }

    return count;
  }

  public getGroupsCount(): number {
    return metaPillCount({
      includeAll: this.includeAllGroups ?? true,
      includeNone: this.includeUngrouped ?? true,
      items: (this.groupIds ?? []).filter((g): g is string => !!g),
    });
  }

  public getLabelsCount(): number {
    return metaPillCount({
      includeAll: this.includeAllLabels ?? true,
      includeNone: this.includeUnlabeled ?? true,
      items: (this.labelIds ?? []).filter((l): l is string => !!l),
    });
  }

  public scrubWorkspaceData() {
    this.groupIds = undefined;
    this.includeAllGroups = true;
    this.includeUngrouped = true;
    this.isCombinedGroupsFilter = undefined;
  }

  public isDateFilterActive() {
    return this.dateType !== DateTypeFilter.ALL;
  }

  /**
   * Resolve the active date filter (preset or custom) to a UTC DateTime range.
   * `startDate`/`endDate` on the model are raw user-locale `MM/dd/yyyy` strings
   * (custom-range picker storage format) — NEVER pass them to a GraphQL
   * `DateTime` field directly; use this resolved range instead.
   */
  public getDateRange(): { start: DateTime; end: DateTime } | undefined {
    const now = DateTime.now();

    const dateRanges: Record<
      DateTypeFilter,
      () => { start: DateTime; end: DateTime } | undefined
    > = {
      [DateTypeFilter.TODAY]: () => {
        const start = now.startOf('day').toUTC();
        const end = now.endOf('day').toUTC();
        return { start, end };
      },
      [DateTypeFilter.THISWEEK]: () => {
        const start = now.minus({ days: 6 }).startOf('day').toUTC();
        const end = now.endOf('day').toUTC();
        return { start, end };
      },
      [DateTypeFilter.THISMONTH]: () => {
        const start = now.minus({ days: 29 }).startOf('day').toUTC();
        const end = now.endOf('day').toUTC();
        return { start, end };
      },
      [DateTypeFilter.CUSTOMDATE]: () => {
        if (!this.startDate || !this.endDate) return undefined;

        const start = DateTime.fromFormat(
          this.startDate,
          DATE_FORMAT_MMDDYYYYLUXON
        );
        const end = DateTime.fromFormat(
          this.endDate,
          DATE_FORMAT_MMDDYYYYLUXON
        );

        if (!start.isValid || !end.isValid) {
          console.error('Invalid start or end date');
          return undefined;
        }

        const startUTC = start.startOf('day').toUTC();
        const endUTC = end.endOf('day').toUTC();

        return { start: startUTC, end: endUTC };
      },

      [DateTypeFilter.ALL]: () => undefined,
    };

    return dateRanges[this.dateType ?? DateTypeFilter.ALL]?.();
  }
}
